import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { formatAddress, toTitleCase } from "@utils/utils";
import { useNavigate } from "react-router-dom";

import { Button, Headline, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import { CreateClinicListingOutput } from "@api/rest/types-and-hooks";

interface InternalVerificationPendingProps {
  clinicData: CreateClinicListingOutput;
}

export const InternalVerificationPending: React.FC<InternalVerificationPendingProps> = ({ clinicData }) => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col w-full items-center my-4 md:my-8">
      <div className="flex flex-col gap-4 w-full max-w-2xl px-4 sm:px-6">
        <div className="flex flex-col items-center">
          <Headline>Verification request received</Headline>
          <Paragraph size="medium">We'll review your request within 1 business day.</Paragraph>
        </div>

        <div className="flex flex-col gap-6 p-8 rounded-lg border bg-white">
          <div className="flex flex-col gap-4">
            <Paragraph className="text-lg!" colorType="primary" displayType="loud">
              Request Summary
            </Paragraph>
            <div className="flex flex-col gap-2 p-4 border rounded-xl bg-limestone-50">
              <div className="flex flex-col">
                <Label size="small" colorType="secondary">
                  Clinic name
                </Label>
                <Paragraph>{clinicData.name}</Paragraph>
              </div>
              <div className="flex flex-col">
                <Label size="small" colorType="secondary">
                  Location
                </Label>
                <Paragraph>
                  {clinicData.location
                    ? formatAddress({
                        addressLine1: toTitleCase(clinicData.location.address_line_1),
                        addressLine2: toTitleCase(clinicData.location.address_line_2) || undefined,
                        city: toTitleCase(clinicData.location.city),
                        state: clinicData.location.state,
                        zip: clinicData.location.postal_code,
                      })
                    : "—"}
                </Paragraph>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <Paragraph className="text-lg!" colorType="primary" displayType="loud">
              What happens next?
            </Paragraph>

            <div className="flex flex-row gap-4">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-icon-highlight text-white">
                1
              </div>
              <div className="flex flex-col">
                <Paragraph displayType="loud" colorType="primary">
                  We review your request
                </Paragraph>
                <Paragraph colorType="primary">Our team will verify the clinic information you submitted.</Paragraph>
              </div>
            </div>

            <div className="flex flex-row gap-4">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-icon-highlight text-white">
                2
              </div>
              <div className="flex flex-col">
                <Paragraph displayType="loud" colorType="primary">
                  We’ll follow up if needed
                </Paragraph>
                <Paragraph colorType="primary">
                  If we need anything else to confirm the listing, we’ll reach out by email.
                </Paragraph>
              </div>
            </div>

            <div className="flex flex-row gap-4">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-icon-highlight text-white">
                3
              </div>
              <div className="flex flex-col">
                <Paragraph displayType="loud" colorType="primary">
                  Clinic added to your org
                </Paragraph>
                <Paragraph colorType="primary">Once approved, this clinic will appear in your clinics list.</Paragraph>
              </div>
            </div>

            <div className="flex flex-row gap-2 p-4 rounded-lg bg-blue-50">
              <EnvelopeIcon className="text-blue-800 size-6 shrink-0" />
              <Paragraph className="text-blue-800!">
                We’ll send updates to your registered email address. Check your spam folder if you don’t see our
                message.
              </Paragraph>
            </div>

            <div className="flex flex-row justify-end">
              <Button text="Back to clinics" onClick={() => navigate("/clinics")} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
